import React from "react";
export default function Technology() {
  return (
    <div style={styles.page}>
      <h1 style={styles.title}>FentaSafe Cognitive Safety Architecture</h1>
      <p style={styles.subtitle}>
        A six-stage AI pipeline design combining environmental risk classification,
        per-decision explanation, memory-driven incident matching, and adaptive
        learning — purpose-built for K-12 school safety and public safety environments.
        This describes the platform's intended architecture; components are at
        varying stages of development, from operational software to early design.
      </p>

      {/* STATUS RIBBON — replaces fabricated performance metrics */}
      <div style={styles.metricsRow}>
        <div style={styles.metric}>
          <span style={styles.metricValueSmall}>Software platform operational</span>
        </div>
        <div style={styles.metric}>
          <span style={styles.metricValueSmall}>Home-based sensor testing in progress</span>
        </div>
        <div style={styles.metric}>
          <span style={styles.metricValueSmall}>Independent validation not yet started</span>
        </div>
        <div style={styles.metric}>
          <span style={styles.metricValueSmall}>No performance metrics published until independently verified</span>
        </div>
      </div>

      {/* DATA SOURCES — only what's actually built or in active testing */}
      <div style={styles.dataSourcesBox}>
        <h3 style={styles.boxTitle}>Data Sources</h3>
        <div style={styles.dataSourcesRow}>
          <div style={styles.dataSource}>📡 IoT Sensors (PM2.5, VOC, Temperature, Humidity)</div>
          <div style={styles.dataSource}>🌎 Environmental Pattern Signals</div>
        </div>
        <p style={{color: "#64748b", fontSize: "0.8rem", textAlign: "center", marginTop: 14}}>
          Additional data source types (e.g. video, expanded telemetry) are potential future
          directions, not current capabilities.
        </p>
      </div>

      {/* COGNITIVE CORE */}
      <div style={styles.core}>
        FentaSafe Cognitive Core — Six-Stage Architecture (Design)
      </div>

      {/* PIPELINE */}
      <div style={styles.pipeline}>
        <div style={styles.agentCard}>
          <div style={styles.agentHeader}>
            <span style={styles.agentNumber}>01</span>
            <span style={styles.agentName}>Risk Prediction</span>
            <span style={styles.agentBadge}>Design Stage</span>
          </div>
          <p style={styles.agentDesc}>
            Intended to be a trained AI risk classifier that learns multi-signal environmental
            patterns from data — moving beyond fixed thresholds to detect subtle, combined
            risk indicators that simple rule-based systems can miss. Not yet trained on
            real-world data.
          </p>
          <div style={styles.agentMetrics}>
            <span style={styles.agentMetric}>4 planned risk levels: SAFE / ELEVATED / WARNING / CRITICAL</span>
          </div>
        </div>
        <div style={styles.connector}>↓</div>
        <div style={styles.agentCard}>
          <div style={styles.agentHeader}>
            <span style={styles.agentNumber}>02</span>
            <span style={styles.agentName}>Decision Explanation</span>
            <span style={styles.agentBadge}>Design Stage</span>
          </div>
          <p style={styles.agentDesc}>
            Intended to identify which specific signal drove each alert — answering
            "why did the system raise this alert?" for individual decisions, not just
            in aggregate. Latency and performance not yet measured.
          </p>
          <div style={styles.agentMetrics}>
            <span style={styles.agentMetric}>Per-signal impact attribution (planned)</span>
            <span style={styles.agentMetric}>Human-readable explanation output (planned)</span>
          </div>
        </div>
        <div style={styles.connector}>↓</div>
        <div style={styles.agentCard}>
          <div style={styles.agentHeader}>
            <span style={styles.agentNumber}>03</span>
            <span style={styles.agentName}>Memory Intelligence</span>
            <span style={styles.agentBadge}>Design Stage</span>
          </div>
          <p style={styles.agentDesc}>
            Intended to compare current conditions against historical incidents to detect
            recurring patterns. Requires a historical incident dataset that does not yet
            exist; not yet built or tested.
          </p>
        </div>
        <div style={styles.connector}>↓</div>
        <div style={styles.agentCard}>
          <div style={styles.agentHeader}>
            <span style={styles.agentNumber}>04</span>
            <span style={styles.agentName}>Decision Intelligence</span>
            <span style={styles.agentBadge}>Design Stage</span>
          </div>
          <p style={styles.agentDesc}>
            Intended to be an auditable decision engine that consumes prediction confidence
            and signal attribution to determine priority and action, inspectable by human
            supervisors. Not yet implemented.
          </p>
        </div>
        <div style={styles.connector}>↓</div>
        <div style={styles.agentCard}>
          <div style={styles.agentHeader}>
            <span style={styles.agentNumber}>05</span>
            <span style={styles.agentName}>Response Recommendation</span>
            <span style={styles.agentBadge}>Design Stage</span>
          </div>
          <p style={styles.agentDesc}>
            Intended to route response recommendations based on the nature of a detected
            environmental risk. Not yet implemented.
          </p>
        </div>
        <div style={styles.connector}>↓</div>
        <div style={styles.agentCard}>
          <div style={styles.agentHeader}>
            <span style={styles.agentNumber}>06</span>
            <span style={styles.agentName}>Adaptive Learning</span>
            <span style={styles.agentBadge}>Design Stage</span>
          </div>
          <p style={styles.agentDesc}>
            Intended to allow operator feedback to refine system sensitivity per facility
            over time. Not yet implemented.
          </p>
        </div>
      </div>

      <div style={styles.highlight}>
        Human-in-the-loop safety AI by design — the intent is for every decision to be
        explainable, auditable, and subject to human review before any action. Every
        alert requires human review; no automated action is taken.
      </div>

      {/* BACKEND INFRASTRUCTURE */}
      <div style={styles.infraBox}>
        <h2 style={styles.boxTitle}>Backend Infrastructure (Design)</h2>
        <p style={{color: "#64748b", fontSize: "0.82rem", textAlign: "center", marginBottom: 16}}>
          The items below describe intended architecture. The software platform is operational
          in a demonstration environment; production-scale infrastructure has not yet been built or tested.
        </p>
        <div style={styles.infraGrid}>
          <div style={styles.infraItem}>
            <strong style={styles.infraLabel}>Sensor Ingestion</strong>
            Intended real-time sensor data streaming via standard IoT protocols (e.g. MQTT)
          </div>
          <div style={styles.infraItem}>
            <strong style={styles.infraLabel}>Risk Engine</strong>
            Intended multi-signal pattern analysis on sensor history
          </div>
          <div style={styles.infraItem}>
            <strong style={styles.infraLabel}>Database</strong>
            Intended cloud-based storage for readings, assessments, and alerts
          </div>
          <div style={styles.infraItem}>
            <strong style={styles.infraLabel}>Notifications</strong>
            Intended alerting for elevated-risk readings
          </div>
          <div style={styles.infraItem}>
            <strong style={styles.infraLabel}>Auth &amp; Access</strong>
            Intended role-based access control
          </div>
        </div>
      </div>

      {/* DEVELOPMENT STATUS — replaces "Platform Readiness" checklist */}
      <div style={styles.infraBox}>
        <h2 style={styles.boxTitle}>Development Status</h2>
        <div style={styles.infraGrid}>
          <div>🔧 Zone-level, privacy-preserving design (no individual tracking) — design principle</div>
          <div>🔧 Human oversight required for every alert — design principle</div>
          <div>🧪 Home-based sensor testing — in progress</div>
          <div>📝 Software dashboard — operational (demonstration environment)</div>
          <div>⏳ Independent lab/field validation — not yet started</div>
          <div>⏳ Production-scale infrastructure — not yet built</div>
        </div>
      </div>

      {/* IP — corrected to avoid unverified scope claims */}
      <div style={styles.infraBox}>
        <h2 style={styles.boxTitle}>Intellectual Property</h2>
        <p style={{color: "#94a3b8", marginBottom: 12, textAlign: "center"}}>
          Indian Patent Application No. 202641022911 (Pending)
        </p>
        <p style={{color: "#64748b", fontSize: "0.85rem", textAlign: "center", maxWidth: 700, margin: "0 auto"}}>
          This application covers FentaSafe's environmental risk detection and alerting
          method and system. The specific scope of claims is determined by the filed
          application and is subject to patent office review; it should not be assumed
          to cover every architectural concept described on this page.
        </p>
      </div>

      {/* EXECUTIVE SUMMARY */}
      <div style={styles.summaryBox}>
        <h2 style={styles.boxTitle}>Executive Technology Summary</h2>
        <p style={styles.summaryText}>
          FentaSafe combines environmental sensing with a planned multi-stage AI architecture
          and human-supervised governance. The platform is designed to transform PM2.5, VOC,
          temperature, and humidity signals into plain-language alerts for human review —
          supporting earlier awareness in K-12 schools and public safety environments.
        </p>
        <p style={styles.summaryText}>
          The software platform is operational in a demonstration environment. Home-based
          testing of physical sensors is underway. Independent laboratory or field validation
          has not yet been completed, and no specific accuracy, latency, or readiness metrics
          are published until they can be independently verified.
        </p>
      </div>
    </div>
  );
}
const styles = {
  page: { minHeight: "100vh", padding: "60px 20px", color: "white", maxWidth: 1100, margin: "0 auto", background: "#020617" },
  title: { textAlign: "center", fontSize: "2.2rem", marginBottom: 16 },
  subtitle: { textAlign: "center", color: "#94a3b8", marginBottom: 32, lineHeight: 1.7, maxWidth: 800, marginLeft: "auto", marginRight: "auto" },
  metricsRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 32 },
  metric: { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", background: "rgba(37, 99, 235, 0.10)", border: "1px solid rgba(37, 99, 235, 0.25)", borderRadius: 10, padding: "16px 12px" },
  metricValue: { fontSize: "1.8rem", fontWeight: 700, color: "#60a5fa", lineHeight: 1.2 },
  metricValueSmall: { fontSize: "0.85rem", fontWeight: 600, color: "#60a5fa", lineHeight: 1.4 },
  metricLabel: { fontSize: "0.75rem", color: "#94a3b8", marginTop: 6, lineHeight: 1.4 },
  dataSourcesBox: { background: "rgba(2,6,23,0.75)", border: "1px solid #1e293b", borderRadius: 12, padding: "20px 24px", marginBottom: 24 },
  boxTitle: { textAlign: "center", marginBottom: 16, fontSize: "1.3rem", fontWeight: 600 },
  dataSourcesRow: { display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 12 },
  dataSource: { background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, padding: "8px 14px", fontSize: "0.85rem", color: "#cbd5e1" },
  core: { margin: "24px auto", padding: "14px 32px", borderRadius: 40, border: "1px solid rgba(59,130,246,0.5)", background: "radial-gradient(circle, rgba(59,130,246,0.2), transparent)", fontWeight: 700, fontSize: "1.1rem", textAlign: "center", letterSpacing: "0.5px", color: "#93c5fd" },
  pipeline: { display: "flex", flexDirection: "column", alignItems: "center", gap: 0, marginBottom: 24 },
  agentCard: { width: "100%", maxWidth: 750, padding: "20px 24px", border: "1px solid #1e293b", borderRadius: 12, background: "rgba(2,6,23,0.85)", textAlign: "left" },
  agentHeader: { display: "flex", alignItems: "center", gap: 12, marginBottom: 10 },
  agentNumber: { fontSize: "0.8rem", color: "#475569", fontWeight: 700, minWidth: 24 },
  agentName: { fontSize: "1.05rem", fontWeight: 700, color: "#e2e8f0", flex: 1 },
  agentBadge: { background: "rgba(148,163,184,0.15)", border: "1px solid rgba(148,163,184,0.35)", borderRadius: 6, padding: "2px 10px", fontSize: "0.75rem", color: "#94a3b8", fontWeight: 600 },
  agentDesc: { color: "#94a3b8", fontSize: "0.88rem", lineHeight: 1.6, marginBottom: 10 },
  agentMetrics: { display: "flex", flexWrap: "wrap", gap: 8 },
  agentMetric: { background: "#0f172a", border: "1px solid #1e293b", borderRadius: 6, padding: "3px 10px", fontSize: "0.75rem", color: "#94a3b8" },
  connector: { color: "#1e40af", fontSize: "1.5rem", lineHeight: 1, margin: "4px 0" },
  highlight: { margin: "24px auto", padding: "16px 24px", border: "1px solid #1e293b", borderRadius: 10, background: "#020617", color: "#94a3b8", maxWidth: 750, textAlign: "center", lineHeight: 1.6 },
  infraBox: { background: "rgba(2,6,23,0.8)", border: "1px solid #1e293b", borderRadius: 14, padding: "28px 32px", marginBottom: 24 },
  infraGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, color: "#cbd5e1", fontSize: "0.9rem", lineHeight: 1.6 },
  infraItem: { background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, padding: "12px 16px" },
  infraLabel: { display: "block", color: "#60a5fa", marginBottom: 6, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.5px" },
  ipItem: { background: "#0f172a", border: "1px solid rgba(251,191,36,0.2)", borderRadius: 8, padding: "12px 16px", color: "#fbbf24", fontWeight: 600, textAlign: "center", fontSize: "0.88rem" },
  summaryBox: { background: "rgba(2,6,23,0.85)", border: "1px solid #1e293b", borderRadius: 14, padding: "32px", marginBottom: 24, textAlign: "center" },
  summaryText: { color: "#cbd5e1", lineHeight: 1.8, maxWidth: 900, margin: "0 auto 12px", fontSize: "0.95rem" },
};